/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Navbar } from './components/Navbar';
import { Home } from './pages/Home';
import Feedback from './pages/Feedback';
import Imprint from './pages/Imprint';
import Impressum from './pages/Impressum';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Datenschutz from './pages/Datenschutz';
import GTC from './pages/GTC';
import AGB from './pages/AGB';
import { GuidesOverview } from './pages/GuidesOverview';
import { SvsSocialInsuranceGuide } from './pages/SvsSocialInsuranceGuide';
import { SvsSozialversicherungGuide } from './pages/SvsSozialversicherungGuide';
import { StartingOutSelfEmployedGuide } from './pages/StartingOutSelfEmployedGuide';
import { SelbststaendigWerdenGuide } from './pages/SelbststaendigWerdenGuide';
import { UmsatzsteuerGrundlagenGuide } from './pages/UmsatzsteuerGrundlagenGuide';
import { VatBasicsGuide } from './pages/VatBasicsGuide';
import { KleinunternehmerregelungGuide } from './pages/KleinunternehmerregelungGuide';
import { SmallBusinessVatExemptionGuide } from './pages/SmallBusinessVatExemptionGuide';

// Not prerendered (no crawler-visible content to preserve) and only reached
// after an email confirmation redirect — safe to split out of the main bundle.
const EmailConfirmed = lazy(() => import('./pages/EmailConfirmed').then(m => ({ default: m.EmailConfirmed })));
const ConfirmedEn = lazy(() => import('./pages/ConfirmedEn').then(m => ({ default: m.ConfirmedEn })));
const ConfirmedDe = lazy(() => import('./pages/ConfirmedDe').then(m => ({ default: m.ConfirmedDe })));
const PortalShort = lazy(() => import('./portal-about/short/PortalShort'));
const PortalLong = lazy(() => import('./portal-about/long/PortalLong'));
const PortalLongEn = lazy(() => import('./portal-about/long-en/PortalLongEn'));
// SVS-Kurzinfo (V2.5 of the year-end-check prototypes; the other variants are in _deprecated/).
const SvsYeV2FlowShort = lazy(() => import('./prototypes/svs-year-end-v2/flow-short/FlowShort'));
// V1 click-through (Umsatzsteuer-Grundlagen / VAT basics) — copied from the
// SVS-Kurzinfo flow-short implementation above, see its own header comment.
const VatBasicsV1FlowShort = lazy(() => import('./prototypes/vat-basics-v1/flow-short/FlowShort'));
// V2 click-through (Kleinunternehmerregelung / small-business VAT exemption)
// — copied from the V1 click-through above, see its own header comment.
const KleinunternehmerV2FlowShort = lazy(() => import('./prototypes/kleinunternehmer-v2/flow-short/FlowShort'));
import { m, LazyMotion, domAnimation, useScroll, useSpring } from 'motion/react';
import { Mail, MapPin, Phone, Instagram } from 'lucide-react';
import { Link } from 'react-router-dom';

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      const timeoutId = setTimeout(() => {
        window.scrollTo(0, 0);
      }, 50);
      return () => clearTimeout(timeoutId);
    } else {
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    }
  }, [pathname, hash]);

  return null;
};

import { useLanguage } from './contexts/LanguageContext';
import { ConsentProvider, useConsent } from './contexts/ConsentContext';
import { ConsentBanner } from './components/ConsentBanner';

const ScrollProgress = ({ isFeedbackPage }: { isFeedbackPage: boolean }) => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 70,
    damping: 15,
    restDelta: 0.01
  });

  if (isFeedbackPage) return null;

  return (
    <m.div
      className="fixed top-0 left-0 right-0 h-1 bg-pstax-blue origin-left z-[100]"
      style={{ scaleX }}
    />
  );
};

const Layout = ({ children }: { children: React.ReactNode }) => {
  const { pathname } = useLocation();
  const theme = useLanguage();
  const { t, language } = theme;
  const commonT = t.common;
  const navT = t.nav;
  const { openSettings } = useConsent();

  const isFeedbackPage =
    pathname.replace(/\/$/, '') === '/feedback' ||
    // Full-screen click-flow pages bring their own chrome.
    /^\/(de\/guides\/svs-sozialversicherung\/kurz|en\/guides\/svs-social-insurance\/short)/.test(pathname) ||
    /^\/(de\/guides\/umsatzsteuer-grundlagen\/kurz|en\/guides\/vat-basics-austria\/short)/.test(pathname) ||
    /^\/(de\/guides\/kleinunternehmerregelung\/kurz|en\/guides\/small-business-vat-exemption\/short)/.test(pathname) ||
    /^\/(de|en)\/pstax-portal-about\/?$/.test(pathname);

  // Global GTM Tracking for any CTA click to #fit-check
  useEffect(() => {
    const handleGlobalClick = (e: MouseEvent) => {
      let target = e.target as HTMLElement | null;
      while (target && target !== document.body) {
        const href = target.getAttribute('href') || target.getAttribute('to');
        const isFitCheckLink = (target.tagName === 'A' || target.hasAttribute('to')) && 
                              (href?.includes('#fit-check') || href?.includes('fit-check'));
        
        if (isFitCheckLink) {
          const ctaText = target.innerText || target.textContent || 'unknown';
          let ctaLocation = target.getAttribute('data-cta-location') || 'unknown';
          
          if (ctaLocation === 'unknown') {
            let parent = target.parentElement;
            while (parent) {
              if (parent.tagName === 'NAV' || parent.id === 'mobile-menu') {
                ctaLocation = 'navigation';
                break;
              } else if (parent.tagName === 'FOOTER') {
                ctaLocation = 'footer';
                break;
              } else if (parent.id === 'pricing') {
                ctaLocation = 'pricing';
                break;
              } else if (parent.id === 'how-we-work' || parent.id === 'workflow') {
                ctaLocation = 'section';
                break;
              } else if (parent.id === 'how-austria-works' || parent.id === 'faq') {
                ctaLocation = 'section';
                break;
              } else if (parent.tagName === 'SECTION') {
                ctaLocation = 'section';
                break;
              }
              parent = parent.parentElement;
            }
          }
          
          if (ctaLocation === 'unknown') {
            const rect = target.getBoundingClientRect();
            if (rect.top + window.scrollY < 1200) {
              ctaLocation = 'hero';
            } else {
              ctaLocation = 'section';
            }
          }

          (window as any).dataLayer = (window as any).dataLayer || [];
          (window as any).dataLayer.push({
            event: 'fit_check_cta_click',
            cta_text: ctaText.trim().replace(/\s+/g, ' '),
            cta_location: ctaLocation,
            page_path: window.location.pathname,
            language: language ? language.toLowerCase() : 'unknown'
          });
          break;
        }
        target = target.parentElement;
      }
    };

    document.addEventListener('click', handleGlobalClick);
    return () => document.removeEventListener('click', handleGlobalClick);
  }, [language]);

  const imprintPath = language === 'EN' ? '/en/imprint/' : '/de/impressum/';
  const privacyPath = language === 'EN' ? '/en/privacy-policy/' : '/de/datenschutz/';
  const gtcPath = language === 'EN' ? '/en/gtc/' : '/de/agb/';

  return (
    <LazyMotion features={domAnimation}>
    <div className="min-h-screen bg-pstax-bg text-pstax-navy selection:bg-pstax-blue selection:text-pstax-navy font-sans overflow-x-hidden relative w-full max-w-full">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <ScrollProgress isFeedbackPage={isFeedbackPage} />
      
      {!isFeedbackPage && <Navbar />}
      
      <main id="main-content" className="relative w-full max-w-full">
        {children}
      </main>

      {!isFeedbackPage && (
        <footer className="bg-pstax-navy text-white pt-24 pb-12">
          <div className="container mx-auto px-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
              <div className="col-span-2">
                <img 
                  src="/regenerated_image_1777641628772.webp" 
                  alt="PSTax - Digital Tax Advisory Austria" 
                  className="h-12 mb-8 md:brightness-0 md:invert" 
                  loading="lazy"
                  decoding="async"
                />
                <p className="text-white/60 max-w-sm mb-8 leading-relaxed">
                  {commonT.footer}
                </p>
                <div className="flex gap-4">
                  <a href="https://www.instagram.com/pascal_pstax/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-pstax-blue hover:text-pstax-navy transition-all duration-300" aria-label="PSTax on Instagram">
                    <Instagram size={18} />
                  </a>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-bold mb-8 text-pstax-blue">{navT.contact || 'Contact'}</h4>
                <ul className="space-y-4 text-white/80 text-sm">
                  <li className="flex items-center gap-3">
                    <Mail size={16} className="text-pstax-blue shrink-0" />
                    <a href="mailto:office@pstax.at" className="hover:text-pstax-blue transition-colors">office@pstax.at</a>
                  </li>
                  <li className="flex items-center gap-3">
                    <Phone size={16} className="text-pstax-blue shrink-0" />
                    <a href="tel:+4369911599396" className="hover:text-pstax-blue transition-colors">+43 699 1159 9396</a>
                  </li>
                  <li className="flex items-start gap-3">
                    <MapPin size={16} className="text-pstax-blue shrink-0 mt-0.5" />
                    <span>{navT.companyAddress}</span>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-bold mb-8 text-pstax-blue">{navT.legal || 'Legal'}</h4>
                <ul className="space-y-4 text-white/80 text-sm">
                  <li><Link to={imprintPath} className="hover:text-pstax-blue transition-colors">{navT.imprint || 'Imprint'}</Link></li>
                  <li><Link to={privacyPath} className="hover:text-pstax-blue transition-colors">{navT.privacy || 'Privacy Policy'}</Link></li>
                  <li><Link to={gtcPath} className="hover:text-pstax-blue transition-colors">{navT.gtc || 'General Terms and Conditions'}</Link></li>
                  <li>
                    <button 
                      onClick={openSettings} 
                      className="hover:text-pstax-blue transition-colors text-left"
                    >
                      {language === 'DE' ? 'Cookie-Einstellungen' : 'Cookie Settings'}
                    </button>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="pt-12 border-t border-white/10 text-center text-white/80 text-sm">
              <div className="mb-6 space-y-1 leading-relaxed font-normal">
                <p className="font-semibold text-white">PSTax Steuerberatung FlexCo</p>
                <p>{navT.companyAddress}</p>
                <p>{navT.kswMember}</p>
                <p>{navT.supervisoryAuthority}</p>
                <p>{navT.professionalLaw}</p>
              </div>
              <p className="text-white/40 text-xs">&copy; {new Date().getFullYear()} PSTax - Pascal Schraml. {navT.rightsReserved || 'All rights reserved.'}</p>
            </div>
          </div>
        </footer>
      )}
      <ConsentBanner />
    </div>
    </LazyMotion>
  );
};

import { LanguageProvider } from './contexts/LanguageContext';

// Synchronously handle shortcut URLs on page load before React Router initializes
if (typeof window !== 'undefined') {
  const rawPath = window.location.pathname;
  const cleanPath = rawPath.toLowerCase().replace(/\/$/, '');
  if (cleanPath === '/fit-check' || cleanPath === '/calculator') {
    const targetId = cleanPath === '/fit-check' ? 'fit-check' : 'calculator';
    const search = window.location.search;
    // No language signal on these bare shortcut URLs — land on the German
    // default homepage, same as any other unprefixed path.
    const newUrl = `/de/${search}#${targetId}`;
    window.history.replaceState(null, '', newUrl);
  } else if (/^\/(de|en)(\/.*)?$/.test(rawPath) && !rawPath.endsWith('/')) {
    // Canonicalize DE/EN routes to a trailing slash (matches the landingpages
    // URL convention — see App.tsx's <Routes>) *before* React Router
    // evaluates the location. The prerendered HTML for these routes is always
    // built for the trailing-slash path (see prerenderRoutes.ts), so without
    // this the client would hydrate against a location React Router can't
    // match (a real mismatch, not just a cosmetic one).
    window.history.replaceState(null, '', `${rawPath}/${window.location.search}${window.location.hash}`);
  }
}

// Redirect helper component for client-side routing fallback of shortcut URLs
const ShortcutRedirect = ({ target }: { target: string }) => {
  const { search } = useLocation();
  return <Navigate to={`/de/${search}#${target}`} replace />;
};

// Catch-all redirect component to preserve query string (e.g., gclid, utm_*) and hash on unknown routes
const CatchAllRedirect = () => {
  const { pathname, search, hash } = useLocation();
  const home = pathname.startsWith('/en') ? '/en/' : '/de/';
  return <Navigate to={`${home}${search}${hash}`} replace />;
};

function App() {
  // Load GTM here rather than via a requestIdleCallback/setTimeout in index.html —
  // this effect only runs once React has fully committed/hydrated, so GTM's
  // injected scripts can never touch the DOM mid-hydration (see index.html's
  // comment on window.pstaxLoadGTM for the failure mode this avoids). The extra
  // setTimeout pushes it one macrotask further out, giving any concurrent
  // hydration work still happening elsewhere in the tree (e.g. behind a Suspense
  // boundary) more room to settle before GTM's scripts start running.
  useEffect(() => {
    const id = setTimeout(() => {
      (window as any).pstaxLoadGTM?.();
    }, 0);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    let activeIntervalId: any = null;

    const handleHashScroll = () => {
      if (activeIntervalId) {
        clearInterval(activeIntervalId);
        activeIntervalId = null;
      }

      const hash = window.location.hash;
      if (!hash) return;
      const id = decodeURIComponent(hash.replace('#', ''));
      if (!id) return;

      const startTime = Date.now();
      let lastScrollY = window.scrollY;

      activeIntervalId = setInterval(() => {
        const element = document.getElementById(id);
        if (element) {
          const rect = element.getBoundingClientRect();
          // Sticky header scroll-margin-top is 96px
          const targetTop = 96;
          const isAtTarget = Math.abs(rect.top - targetTop) < 5;
          const isAtBottom = window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 5;

          if (isAtTarget || isAtBottom) {
            if (activeIntervalId) {
              clearInterval(activeIntervalId);
              activeIntervalId = null;
            }
            return;
          }

          const currentScrollY = window.scrollY;
          const isMoving = Math.abs(currentScrollY - lastScrollY) > 1;
          lastScrollY = currentScrollY;

          // If not currently animating, trigger or adjust the smooth scroll
          if (!isMoving) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }

        if (Date.now() - startTime > 3000) {
          if (activeIntervalId) {
            clearInterval(activeIntervalId);
            activeIntervalId = null;
          }
        }
      }, 100);
    };

    // Run on mount
    handleHashScroll();

    window.addEventListener('hashchange', handleHashScroll);
    return () => {
      if (activeIntervalId) {
        clearInterval(activeIntervalId);
      }
      window.removeEventListener('hashchange', handleHashScroll);
    };
  }, []);

  return (
    <ConsentProvider>
      <LanguageProvider>
        <ScrollToTop />
        <Layout>
          <Suspense fallback={null}>
          <Routes>
          <Route path="/de/" element={<Home />} />
          <Route path="/en/" element={<Home />} />
          <Route path="/fit-check" element={<ShortcutRedirect target="fit-check" />} />
          <Route path="/fit-check/" element={<ShortcutRedirect target="fit-check" />} />
          <Route path="/calculator" element={<ShortcutRedirect target="calculator" />} />
          <Route path="/calculator/" element={<ShortcutRedirect target="calculator" />} />
          <Route path="/feedback" element={<Feedback />} />
          <Route path="/en/imprint/" element={<Imprint />} />
          <Route path="/de/impressum/" element={<Impressum />} />
          <Route path="/en/privacy-policy/" element={<PrivacyPolicy />} />
          <Route path="/de/datenschutz/" element={<Datenschutz />} />
          <Route path="/en/gtc/" element={<GTC />} />
          <Route path="/de/agb/" element={<AGB />} />
          <Route path="/email-confirmed" element={<EmailConfirmed />} />
          <Route path="/confirmed" element={<ConfirmedEn />} />
          <Route path="/bestaetigt" element={<ConfirmedDe />} />
          <Route path="/de/guides/" element={<GuidesOverview lang="de" />} />
          <Route path="/en/guides/" element={<GuidesOverview lang="en" />} />
          <Route path="/en/guides/svs-social-insurance/" element={<SvsSocialInsuranceGuide />} />
          <Route path="/de/guides/svs-sozialversicherung/" element={<SvsSozialversicherungGuide />} />
          <Route path="/en/guides/starting-out-self-employed/" element={<StartingOutSelfEmployedGuide />} />
          <Route path="/de/guides/selbststaendig-werden/" element={<SelbststaendigWerdenGuide />} />
          <Route path="/de/guides/umsatzsteuer-grundlagen/" element={<UmsatzsteuerGrundlagenGuide />} />
          <Route path="/en/guides/vat-basics-austria/" element={<VatBasicsGuide />} />
          <Route path="/de/guides/kleinunternehmerregelung/" element={<KleinunternehmerregelungGuide />} />
          <Route path="/en/guides/small-business-vat-exemption/" element={<SmallBusinessVatExemptionGuide />} />
          {/* Quick Click-Throughs of the published articles (src/articles.ts). The SVS one
              (V2.5, also linked from the newsletter) moved here from /de/svs-kurzinfo/ and
              /en/svs-shortinfo/ — those 301 via legacyUrlRedirects.ts. */}
          <Route path="/de/guides/svs-sozialversicherung/kurz/" element={<SvsYeV2FlowShort lang="de" />} />
          <Route path="/en/guides/svs-social-insurance/short/" element={<SvsYeV2FlowShort lang="en" />} />
          <Route path="/de/guides/umsatzsteuer-grundlagen/kurz/" element={<VatBasicsV1FlowShort lang="de" />} />
          <Route path="/en/guides/vat-basics-austria/short/" element={<VatBasicsV1FlowShort lang="en" />} />
          <Route path="/de/guides/kleinunternehmerregelung/kurz/" element={<KleinunternehmerV2FlowShort lang="de" />} />
          <Route path="/en/guides/small-business-vat-exemption/short/" element={<KleinunternehmerV2FlowShort lang="en" />} />
          <Route path="/de/pstax-portal-about/" element={<PortalShort />} />
          <Route path="/de/pstax-portal-about-long/" element={<PortalLong />} />
          <Route path="/en/pstax-portal-about/" element={<PortalShort lang="en" />} />
          <Route path="/en/pstax-portal-about-long/" element={<PortalLongEn />} />
          <Route path="*" element={<CatchAllRedirect />} />
          </Routes>
          </Suspense>
        </Layout>
      </LanguageProvider>
    </ConsentProvider>
  );
}

export default App;
